// SOAP関連の型定義

// SOAPコンテンツアイテムの型
//
// is_by_ai は legacy (SOAP prompt 1.0.x) 経路でサーバが付与する AI 補完フラグ.
// v2.0.0 経路では新規生成データに is_by_ai=true は乗らない (yakureki-back の
// SOAPEnhancementProcess を v2.x で skip しているため). 既存 v1.x データの混在を
// 吸収するため optional としてのみ受け取り, UI では参照しない方針.
//
// TODO(PHASE-3-案B): yakureki-back の案 B (Discriminated Union 化) 移行と合わせて
// SoapContentItem を SoapContentItemLegacy / SoapContentItemV2 に分離する.
// 関連 TODO は `grep -rn "PHASE-3-案B"` で網羅できる.
export type SoapContentItem = {
  content: string
  is_by_ai?: boolean
  is_selected: boolean
}

// SOAPコンテンツのみの型（ID除外）
export type SoapContent = {
  subjectives: SoapContentItem[]
  objectives: SoapContentItem[]
  assessments: SoapContentItem[]
  educational_plans: SoapContentItem[]
  care_plans: SoapContentItem[]
  observational_plans: SoapContentItem[]
}

// 在宅(訪問報告書)の訪問時状況。各キーは SoapContentItem[] (= SOAP と同じ item 形)。
// 会話 + 薬剤師メモ + SOAP から LLM 生成 (言及が無いキーは空配列で届き、薬剤師が
// 訪問時確認で補完する)。順序は表示順そのまま。back の HOME_VISIT_SITUATION_KEYS と 1:1。
export type HomeVisitFields = {
  residual_drugs: SoapContentItem[] // 服薬・残薬状況
  meal: SoapContentItem[] // 食事
  excretion: SoapContentItem[] // 排泄
  sleep: SoapContentItem[] // 睡眠
}

// SOAP 6 section のキー一覧 (表示順)。keyof SoapContent は型であって値ではないため、
// 「全 section を走査する」処理はこの配列を使う。back の SoapContent.SOAP_SECTION_FIELDS と 1:1。
// sidecar (followup_plans / physician_message 等) も配列値を持つので、Object.keys で
// 走査すると section ではないものまで巻き込む — 走査の正典はここ 1 箇所に置く。
export const SOAP_SECTION_KEYS: (keyof SoapContent)[] = [
  'subjectives',
  'objectives',
  'assessments',
  'educational_plans',
  'care_plans',
  'observational_plans',
]

// ID付きSOAP（APIレスポンス用）
//
// followup_plans (FP: フォローアップ計画) は SOAP 6 section とは質的に別軸
// (来局を待たない継続フォロー)。SoapContent には含めない — SoapSectionKeys =
// keyof SoapContent のため、含めると Selection / SoapSelection / SelectionPayload /
// SoapLabels 等の section 機構に波及してしまう。API レスポンス型 (Soaps) にのみ持たせ、
// 表示は専用 panel で行う (section の選択/コピー/編集機構は通さない)。
//
// home_visit_fields / physician_message は在宅(訪問報告書)のサイドカー。
// followup_plans と同じ方針で SoapContent には含めず Soaps にのみ持たせる。session_type=home_visit
// のときだけ非 null で、front は event.session_type で描画を gate する。
// どちらも SOAPItemDict[] 形。形の違う値をこのサイドカー群に混ぜないこと。
//
// home_visit_generated_at は AI 生成が着地した時刻 (ISO 文字列)。null = 未着。
// 「生成が終わったか」を上の 2 つの形から推測しないための明示フラグで、front の猶予 polling は
// これだけを見る (薬剤師が空欄期間に 1 行編集すると形は埋まるが、生成は未着のまま)。
export type Soaps = {
  id: string
  followup_plans?: SoapContentItem[]
  home_visit_fields?: HomeVisitFields | null
  physician_message?: SoapContentItem[] | null
  home_visit_generated_at?: string | null
  // 報告書を作り直している (資料の「反映する」の後など。back が裏で回す数十秒)。true の間は取り直す
  home_visit_generating?: boolean
  // 生成が読んだ資料と読めずに飛ばした資料の id (SOAP と訪問報告書は別に読む)。null は「残していない」(列を足す前の版)
  attachment_ids?: string[] | null
  skipped_attachment_ids?: string[] | null
  home_visit_attachment_ids?: string[] | null
  home_visit_skipped_attachment_ids?: string[] | null
} & SoapContent

// 選択状態を持つSOAPアイテムの型
// TODO(PHASE-3-案B): SoapContentItem の Discriminated Union 化と合わせて is_by_ai を撤去.
export type SoapItem = {
  content: string // テキスト内容
  is_by_ai?: boolean // legacy 互換 (v2.x では使用しない. 上記 SoapContentItem 参照)
  index: number // 元の配列でのインデックス
}

// SOAPセクションのキー名を定義
export type SoapSectionKeys = keyof SoapContent

// インデックス付きの選択状態型
// 重複テキストを正確に管理するために使用
export type SoapSelection = {
  [key in SoapSectionKeys]: SoapItem[]
}

// YakurekiSoapタイプ（SoapContentのエイリアス）
export type YakurekiSoap = SoapContent

export type SoapLabels = {
  id: string
} & {
  [key in SoapSectionKeys]: string
}

// ===== 新選択状態型（ADR-055: TanStack Query + Optimistic UI 移行用）=====

/**
 * 新しい選択状態の型（インデックスのみ、content は持たない）
 *
 * 設計意図:
 * - content をキャッシュせず、常にマスターデータ（yakurekiDetail.soaps）から取得
 * - Set<number> による O(1) 検索
 * - index: -1 はヘッダー選択を表す
 *
 * @see ADR-055: SelectionContext の Content 同期問題の根本的解決
 */
export type Selection = {
  [key in SoapSectionKeys]: Set<number>
}

/**
 * ヘッダー選択状態
 *
 * 各セクションのヘッダー（例: 【S】）が選択されているかを管理
 */
export type HeaderSelection = Set<SoapSectionKeys>

/**
 * サーバーに送信する選択状態の形式
 *
 * Set<number> は JSON にシリアライズできないため、配列に変換
 */
export type SelectionPayload = {
  [key in SoapSectionKeys]: number[]
}
